import React, { ForwardRefRenderFunction, forwardRef, useImperativeHandle, useState } from "react";

import { Grid, MenuItem } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { AiOutlineInfoCircle } from "react-icons/ai";
import * as yup from "yup";

import { Input, Select } from "../../../../../../../../../components/Form";
import Checkbox from "../../../../../../../../../components/Form/Checkbox";
import Modal from "../../../../../../../../../components/Generic/Modal";
import Skeleton from "../../../../../../../../../components/Generic/Skeleton";
import useYupValidation from "../../../../../../../../../hooks/useYupValidation";
import { Carrier } from "../../../../../../../../../models/carrier";
import TrackableByService from "../../../../../../../../../services/trackable.by.service";
import { CarrierType, Status } from "../../../../../../../../../util/enums";
import useStyles, { CheckboxWrapper, ErrorMessage, ErrorWrapper, Label } from "./styles";

interface CarrierModalProps {
  onSubmit: (carrier?: Carrier) => void;
  isLoading?: boolean;
}

export interface CarrierModalHandle {
  open: (carrier?: Carrier) => void;
  close: () => void;
}

const CreateModal: ForwardRefRenderFunction<CarrierModalHandle, CarrierModalProps> = (
  props,
  ref
) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { onSubmit, isLoading = false } = props;

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [carrier, setCarrier] = useState<Carrier>();
  const [error, setError] = useState<yup.ValidationError[]>([]);

  const { getErrorByField, fieldHasError } = useYupValidation(error);

  const onClearFields = () => {
    setCarrier(undefined);
    setError([]);
  };

  const onCloseModal = () => {
    onClearFields();
    setOpenModal(false);
  };

  const onOpenModal = (data?: Carrier) => {
    setOpenModal(true);
    setCarrier(data);
  };

  const schema = yup.object().shape({
    name: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    scac: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    status: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    type: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    trackableBy: yup
      .array()
      .min(1)
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
  });

  const onValidateSchema = (data?: Carrier) => {
    try {
      schema.validateSync(
        {
          name: data?.name,
          scac: data?.scac,
          status: data?.status,
          type: data?.type,
          trackableBy: data?.trackableBy,
        },
        { abortEarly: false }
      );
      setError([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setError(error?.inner);

      return false;
    }
  };

  const onSubmitData = () => {
    if (!onValidateSchema(carrier)) return;
    onSubmit(carrier);
  };

  const { data: trackableByResponse, isFetching } = useQuery(
    ["get.all.trackable.by"],
    () =>
      TrackableByService.getAll({
        status: Status.ACTIVE,
      }),
    {
      enabled: !!openModal,
      refetchOnWindowFocus: false,
    }
  );

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  return (
    <Modal
      open={openModal}
      handleClose={onCloseModal}
      title={carrier?.id ? translate("editCarrier") : translate("createNewCarrier")}
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        dataCySave: "submitChange",
        onCancel: onCloseModal,
        saveDisabled: isLoading,
        loading: isLoading,
        textSave: carrier?.id ? translate("update") : translate("create"),
        onSave: onSubmitData,
      }}
      className={classes.modalWidth}
    >
      <section className={classes.sectionWidth}>
        <Grid container direction="column" spacing={3}>
          <Grid item container md={12} lg={12} spacing={2}>
            <Grid item md={12} lg={6}>
              <Input
                required
                label={translate("scacCode")}
                placeholder=""
                value={carrier?.scac ?? ""}
                onChange={(event) => {
                  setCarrier(
                    (state) => ({ ...(state || {}), scac: event.target.value } as Carrier)
                  );
                }}
                error={fieldHasError("scac")}
                errorMessage={getErrorByField("scac")}
              />
            </Grid>
            <Grid item md={12} lg={6}>
              <Select
                name="type"
                label={translate("type")}
                required
                value={carrier?.type}
                error={fieldHasError("type")}
                errorMessage={getErrorByField("type")}
                onChange={(event) =>
                  setCarrier(
                    (state) => ({ ...(state ?? {}), type: event.target.value } as Carrier)
                  )
                }
              >
                <MenuItem value={CarrierType.AIR}>{translate("air")}</MenuItem>
                <MenuItem value={CarrierType.SEA}>{translate("sea")}</MenuItem>
              </Select>
            </Grid>
          </Grid>
          <Grid item container md={12} lg={12} spacing={2}>
            <Grid item md={12} lg={6}>
              <Input
                required
                label={translate("carrierName")}
                placeholder=""
                value={carrier?.name ?? ""}
                onChange={(event) => {
                  setCarrier(
                    (state) => ({ ...(state || {}), name: event.target.value } as Carrier)
                  );
                }}
                error={fieldHasError("name")}
                errorMessage={getErrorByField("name")}
              />
            </Grid>
            <Grid item md={12} lg={6}>
              <Input
                label={translate("carrierNameAbbreviation")}
                placeholder=""
                value={carrier?.abreviation ?? ""}
                onChange={(event) => {
                  setCarrier(
                    (state) => ({ ...(state || {}), abreviation: event.target.value } as Carrier)
                  );
                }}
                error={fieldHasError("abreviation")}
                errorMessage={getErrorByField("abreviation")}
              />
            </Grid>
          </Grid>
          <Grid item container md={12} lg={12} spacing={2}>
            <Grid item md={12} lg={12}>
              <ErrorWrapper>
                <Label>
                  {translate("trackableBy")}
                  <span>*</span>
                </Label>
                {fieldHasError("trackableBy") ? (
                  <ErrorMessage>
                    <AiOutlineInfoCircle size={16} />
                    {getErrorByField("trackableBy")}
                  </ErrorMessage>
                ) : null}
              </ErrorWrapper>
              <Skeleton isLoading={isFetching} classesName={classes.skeletonWidth}>
                <CheckboxWrapper>
                  {trackableByResponse?.data.map((option) => (
                    <Checkbox
                      key={option.id}
                      checked={
                        !!carrier?.trackableBy?.find((trackable) => trackable.id === option.id)
                      }
                      label={`${option.name} - ${option.code}`}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setCarrier(
                            (state) =>
                            ({
                              ...(state || {}),
                              trackableBy: [...(state?.trackableBy || []), option],
                            } as Carrier)
                          );
                        } else {
                          const trackableByClone = _.cloneDeep(carrier?.trackableBy);
                          const index = trackableByClone?.findIndex(
                            (trackable) => trackable.id === option.id
                          );

                          trackableByClone?.splice(Number(index), 1);

                          setCarrier(
                            (state) =>
                            ({
                              ...(state || {}),
                              trackableBy: trackableByClone,
                            } as Carrier)
                          );
                        }
                      }}
                    />
                  ))}
                </CheckboxWrapper>
              </Skeleton>
            </Grid>
          </Grid>
          <Grid item container md={12} lg={12}>
            <Grid item md={12} lg={4}>
              <Select
                name="status"
                label={translate("status")}
                required
                value={carrier?.status}
                error={fieldHasError("status")}
                errorMessage={getErrorByField("status")}
                onChange={(event) =>
                  setCarrier(
                    (state) => ({ ...(state ?? {}), status: event.target.value } as Carrier)
                  )
                }
              >
                <MenuItem value={Status.ACTIVE}>{translate("active")}</MenuItem>
                <MenuItem value={Status.INACTIVE}>{translate("inactive")}</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default forwardRef(CreateModal);
